import { formatDate } from 'date-fns';
import { capitalize, isArray, isNull, isString } from 'lodash';

export interface IOption {
    label: string;
    value: string;
    group?: string; // Optional group property for grouping
    groupLabel?: string; // Optional group property for grouping
}

const specialFields = [
    'allYears',
    'freshGraduate',
    'professions',
    'languageIds',
    'softSkillIds',
    'specialties',
    'distance',
    'location',
];
// Utility functions for type checking
export const isDateRange = value => {
    return value && typeof value === 'object' && 'start' in value && 'end' in value;
};
export const isNumberRange = value => {
    return value && typeof value === 'object' && 'min' in value && 'max' in value;
};
export const isSpecialCase = value => {
    if (value && typeof value === 'object') {
        return Object.keys(value).some(key => specialFields.includes(key));
    }
};

export const hasValue = value => {
    if (isString(value)) {
        return value.trim() !== '';
    }
    if (isArray(value)) {
        return value.length > 0;
    }
    if (isDateRange(value)) {
        return value.start || value.end;
    }
    if (isNumberRange(value)) {
        return value.min || value.max;
    }

    if (isSpecialCase(value)) {
        return Object.keys(value).some(key => {
            if (Array.isArray(value[key])) {
                return value[key].length > 0;
            }

            if (key === 'location') {
                return null;
            }
            if (key === 'distance' && value[key] === 0) {
                return null;
            }
            return value[key];
        });
    }
    if (typeof value === 'boolean') return true;
    return Boolean(value);
};

// Function to format the value based on its type
export const renderValue = ({
    label,
    value,
    key,
    suffix,
    chipLabels,
}: {
    value: any;
    key?: string;
    label?: string;
    suffix?: string;
    chipLabels?: Record<string, any>;
}) => {
    if (isString(value)) {
        if (chipLabels?.[key] && Array.isArray(chipLabels?.[key])) {
            const selectedVal = chipLabels?.[key].filter(option => option.value === value);
            return `${label || key}: ${selectedVal.map(val => val.label).join(', ')}`;
        }
        return `${label || key}: ${capitalize(value)}`;
    }

    if (isArray(value)) {
        return `${value.map(item => item.suffix).join(', ')}`;
    }

    if (isDateRange(value)) {
        if (isNull(value.start) && isNull(value.end)) return `All`;
        if (value.start && isNull(value.end)) return `Du ${formatDate(value.start, 'dd/MM/yyyy')}`;
        if (isNull(value.start) && value.end) return `Au ${formatDate(value.end, 'dd/MM/yyyy')}`;
        return `${formatDate(value.start, 'dd/MM/yyyy')} au ${formatDate(value.end, 'dd/MM/yyyy')}`;
    }

    if (isNumberRange(value)) {
        if (isNull(value.min) && isNull(value.max)) return `${label ? label + ': ' : ''}All`;
        if (value.min && isNull(value.max)) return `${label ? label + ': ' : ''}Min ${value.min}`;
        if (isNull(value.min) && value.max) return `${label ? label + ': ' : ''}Max ${value.max}`;
        return `${label ? label + ': ' : ''}${value.min} à ${value.max}`;
    }

    if ('groupLabel' in value) {
        return `${value.groupLabel} - ${value.label}`;
    }

    if ('value' in value) {
        return `${value.label}`;
    }

    if (isSpecialCase(value)) {
        return Object.keys(value).map(key => {
            if (Array.isArray(value[key])) {
                if (chipLabels?.[key] && Array.isArray(chipLabels?.[key])) {
                    const selectedVals = chipLabels?.[key].filter(option => value[key].includes(option.id));
                    return value[key].length > 0 ? `${key}: ${selectedVals.map(val => val.label).join(', ')} ` : '';
                }
                return value[key].length > 0 ? `${key}: ${value[key].join(', ')} ` : '';
            } else {
                if (typeof value[key] === 'object') {
                    if (key === 'location') {
                        return '';
                    }
                }
                return `${key}: ${value[key]} `;
            }
        });
    }

    return `${JSON.stringify(value)}`;
};

export function flattenOptions(data: any[] = []): IOption[] {
    const flattened: IOption[] = [];

    data?.forEach(item => {
        // Add the group label for the main option
        const groupLabel = item.name;

        // Flatten the children
        item.children?.forEach(child => {
            flattened.push({
                label: child.name,
                value: child.id,
                group: item.id, // Use the parent's id as the group
                groupLabel: groupLabel, // Use the parent's name as the group label
            });
        });
    });

    return flattened;
}
